import { BrButton, BrCard, BrDivider, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import AnmSwitch from 'components/anmSwitch/anmSwitch';
import AtualizarInformacoes from 'components/atualizarInformacoes/atualizarInformacoes';
import ErrorMessage from 'components/errorMessage/errorMessage';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { EBaseCalculoDetalhe } from 'enums/EBaseCalculo';
import { useNotificacao } from 'hooks/useNotificacao';
import {
  IEstrutura,
  IEstruturaEnviar,
  IFerrovia,
  IFerroviaEnviar,
  IOperacaoPortuaria,
  IOperacaoPortuariaEnviar,
} from 'interfaces/IAfetacoes';
import { IAtualizarInformacoesEnviar } from 'interfaces/IAtualizarInfo';
import { NomesPaginasOutrosRegimes, PaginasOutrosRegimes } from 'interfaces/IBasesCalculo';
import { IDeducaoAdicionada, IDeducoes } from 'interfaces/IDeducao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INcmListagem } from 'interfaces/INcm';
import { IOutrasBasesNcmEnviar, IOutrasBasesNcmReceber } from 'interfaces/IOutrasBases';
import { ITipoDeUsoLista } from 'interfaces/ITipodeUso';
import { useBackEndRequests as backendTitular } from 'pages/TitularArrendatario/lib/backEndRequests';
import { useBackEndRequests as backEndGeral } from 'pages/components/lib/backEndRequests';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import { Tooltip } from 'react-tooltip';
import { converterDeducoesParaArrayString } from 'utils/converterDeducoesParaString';
import { formatarCompetenciaPorMesEAno } from 'utils/formatCompetencia';
import { formatCurrency, removeFormatCurrency } from 'utils/formatCurrency';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { formatPercentual } from 'utils/formatPercentual';
import { getHintText } from 'utils/getHintText';
import NcmAccordion from '../ncmAccordion/ncmAccordion';
import ModalidadeAfetacao from './modalidadeAfetacao/modalidadeAfetacaoForm';
import styles from './outrasBaseCalculoForm.module.scss';
import { validarOutrasBasesCalculoForm } from './outrasBaseCalculoFormValidacoes';

interface IOutrasBaseCalculoFormProps {
  setIsAdicaoNovaBaseCalculo: (isOpen: boolean) => void;
  setIsEdicao: (isEdicao: boolean) => void;
  nomeBaseCalculo: PaginasOutrosRegimes;
  isEdicao: boolean;
}

const OutrasBaseCalculoForm = ({
  setIsAdicaoNovaBaseCalculo,
  setIsEdicao,
  nomeBaseCalculo,
  isEdicao,
}: IOutrasBaseCalculoFormProps) => {
  const COMPRAS_DE_TERCEIRO = 'COMPRAS DE TERCEIRO';

  const [codigoNCM, setCodigoNCM] = useState<string>('');
  const [receitaBruta, setReceitaBruta] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');
  const [unidadeMedida, setUnidadeMedida] = useState<string>('');
  const [haCompraTerceiro, setHaCompraTerceiro] = useState<boolean>(false);
  const [deducoesAdicionadas, setDeducoesAdicionadas] = useState<IDeducaoAdicionada[]>([]);
  const [isRejeito, setIsRejeito] = useState(false);
  const [hasUsoEspecifico, setHasUsoEspecifico] = useState(false);

  const [hasFerrovia, setHasFerrovia] = useState(false);
  const [hasDutovia, setHasDutovia] = useState(false);
  const [hasOperacoesPortuarias, setHasOperacoesPortuarias] = useState(false);
  const [hasEstruturasFora, setHasEstruturasFora] = useState(false);

  const [ferroviasAdicionadas, setFerroviasAdicionadas] = useState<IFerrovia[] | []>([]);
  const [operacoesPortuariasAdicionadas, setOperacoesPortuariasAdicionadas] = useState<IOperacaoPortuaria[] | []>([]);
  const [estruturasAdicionadas, setEstruturasAdicionadas] = useState<IEstrutura[] | []>([]);

  const [isLoadingPostBase, setIsLoadingPostBase] = useState(false);

  const [isLoadingPostNcm, setIsLoadingPostNcm] = useState(false);

  const [isEditNcm, setIsEditNcm] = useState(false);

  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [info, setInfo] = useState<
    { label: string; value: number | string; format?: Function; hintText?: ReactNode }[]
  >([
    { label: 'Base de Cálculo(R$)', value: 0 },
    {
      label: 'Tipo de Alíquota',
      value: '-',
      format: (value: string) => value,
      hintText: (
        <div className={styles.hintTextContainer}>
          <div>
            Os tipos de alíquota definidos guardam correspondência com as hipóteses previstas na Lei nº 8.001/1990.
            Assim, considera-se:
          </div>
          <div>
            <b>Normal:</b> a alíquota diretamente vinculada à substância mineral, conforme ANEXO da Lei nº 8.001/1990,
            aplicada a todas as operações não sujeitas à uso específico e/ou à redução de 50% prevista no art. 6º, § 7º,
            da Lei nº 7.990/1989.
          </div>
          <div>
            <b>Específica:</b> a alíquota de 1%, aplicável apenas às substâncias relacionadas no ANEXO da Lei nº
            8.001/1990 cuja operação se enquadre em uma das condições: "destinada ao uso imediato na construção civil"
            ou "rochas ornamentais".
          </div>
          <div>
            <b>Reduzida:</b> a alíquota normal sobre a qual aplicou-se a redução de 50% prevista no art. 6º, § 7º, da
            Lei nº 7.990/1989.
          </div>
          <div>
            <b>Específica Reduzida:</b> a alíquota específica, sobre a qual aplicou-se a redução de 50% prevista no art.
            6º, § 7º, da Lei nº 7.990/1989.
          </div>
        </div>
      ),
    },
    { label: 'Alíquota(%)', value: 0, format: formatPercentual },
    { label: 'CFEM Total a Recolher(R$)', value: 0 },
  ]);

  const [tipoUso, setTipoUso] = useState<number>(-1);
  const [placeHolderTipoUso, setPlaceHolderTipoUso] = useState<string>('Selecione o tipo de uso');
  const [ncmEdicao, setNcmEdicao] = useState<IOutrasBasesNcmReceber | null>(null);

  const {
    getDeducoesOutrasBasesCalculo,
    postOutrasBaseDeCalculo,
    postNCMOutrasBasesCalculo,
    getNCMsOutrasBasesCalculo,
    putNCMOutrasBasesCalculo,
    postAtualizarInformacoes,
  } = backendTitular();

  const { getNcmPorGuiaRecolhimento, getUnidadesMedidaPorGuiaRecolhimento, getIsPadraoAguaMineral, getTiposDeUso } =
    backEndGeral();

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const context = useContext(ArrecadacaoContext);
  const competencia = formatarCompetenciaPorMesEAno(context?.competenciaMes || 0, context?.competenciaAno || 0);

  const {
    data: tiposUsoData,
    isFetching: tiposUsoIsLoading,
    error: tiposUsoError,
    refetch: tiposUsoRefetch,
  } = useQuery<ITipoDeUsoLista, IErrorWithSentryId>({
    queryKey: ['tiposUsoQuery', guiaRecolhimentoId],
    queryFn: () => getTiposDeUso(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  const {
    data: deducoesData,
    isLoading: deducoesIsLoading,
    error: deducoesError,
    refetch: deducoesRefetch,
  } = useQuery<IDeducoes, IErrorWithSentryId>({
    queryKey: ['deducoesOutrasBasesCalculoQuery', context?.numeroProcesso, nomeBaseCalculo],
    queryFn: () => getDeducoesOutrasBasesCalculo(guiaRecolhimentoId, EBaseCalculoDetalhe[nomeBaseCalculo].indice),
    refetchOnWindowFocus: false,
  });

  const {
    data: isPadraoAguaMineral,
    isLoading: isPadraoAguaMineralIsLoading,
    error: isPadraoAguaMineralError,
    refetch: isPadraoAguaMineralRefetch,
  } = useQuery<boolean, IErrorWithSentryId>({
    queryKey: ['isPadraoAguaMineralQuery', guiaRecolhimentoId],
    queryFn: () => getIsPadraoAguaMineral(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isPadraoAguaMineral === true) {
      setTipoUso(3);
      setPlaceHolderTipoUso('Águas minerais');
    }
  }, [isPadraoAguaMineral]);

  const mostrarNotificacao = useNotificacao();

  useEffect(() => {
    if (isEditNcm && hasUsoEspecifico === ncmEdicao?.hasUsoEspecifico) {
      setTipoUso(ncmEdicao?.tipoDeUso?.id);
      setPlaceHolderTipoUso(ncmEdicao?.tipoDeUso?.nome || 'Selecione o tipo de uso');
      setNcmEdicao(null);
    } else if (!hasUsoEspecifico && !!tiposUsoData?.tipoDeUsoNaoEspecifico) {
      setTipoUso(tiposUsoData.tipoDeUsoNaoEspecifico[0]?.id);
      setPlaceHolderTipoUso(tiposUsoData.tipoDeUsoNaoEspecifico[0]?.nome || 'Selecione o tipo de uso');
    } else {
      setTipoUso(-1);
      setPlaceHolderTipoUso('Selecione o tipo de uso');
    }

    if (isPadraoAguaMineral) {
      setTipoUso(3);
      setPlaceHolderTipoUso('Águas minerais');
    }
  }, [hasUsoEspecifico, isEditNcm, tiposUsoData]);

  useEffect(() => {
    if (!isEdicao) {
      setIsLoadingPostBase(true);
      const postReceita = async () => {
        await postOutrasBaseDeCalculo(guiaRecolhimentoId, nomeBaseCalculo)
          .then((response) => {
            context?.setIdOutrosRegimes(response.id);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: `Erro ao iniciar ${NomesPaginasOutrosRegimes[nomeBaseCalculo]}, tente novamente.`,
              ...(err && { complementoSentryId: err }),
            });
            setIsEdicao(false);
            setNcmEdicao(null);
            setIsAdicaoNovaBaseCalculo(false);
          });
      };
      postReceita();
      setIsLoadingPostBase(false);
    }
    if (isPadraoAguaMineral === true) {
      setTipoUso(3);
      setPlaceHolderTipoUso('Águas minerais');
    }
  }, []);

  const {
    data: ncmsData,
    isFetching: ncmsIsLoading,
    refetch: ncmsRefetch,
    error: ncmsError,
  } = useQuery<IOutrasBasesNcmReceber[], IErrorWithSentryId>({
    queryKey: ['getNCMsOutrasBasesCalculo', context?.idOutrosRegimes],
    queryFn: () => getNCMsOutrasBasesCalculo(context?.idOutrosRegimes || -1),
    refetchOnWindowFocus: false,
    enabled: context?.idOutrosRegimes !== -10 && context?.idOutrosRegimes !== 0 && !!context?.idOutrosRegimes,
  });

  const limparFormulario = () => {
    setCodigoNCM('');
    setReceitaBruta('');
    setQuantidade('');
    setUnidadeMedida('');
    setHaCompraTerceiro(false);
    setDeducoesAdicionadas([]);
    setIsRejeito(false);
    setHasUsoEspecifico(false);
    setFerroviasAdicionadas([]);
    setOperacoesPortuariasAdicionadas([]);
    setEstruturasAdicionadas([]);
    setHasDutovia(false);
    setHasEstruturasFora(false);
    setHasFerrovia(false);
    setHasOperacoesPortuarias(false);
    setTipoUso(isPadraoAguaMineral === true ? 3 : -1);
    setInfo([
      { label: 'Base de Cálculo(R$)', value: 0 },
      {
        ...info[1],
        label: 'Tipo de Alíquota',
        value: '-',
      },
      { label: 'Alíquota(%)', value: 0, format: formatPercentual },
      { label: 'CFEM Total a Recolher(R$)', value: 0 },
    ]);
  };

  const voltar = () => {
    limparFormulario();
    ncmsRefetch();
    setIsEditNcm(false);
    setNcmEdicao(null);
    context?.setIdNCMOutrosRegimes(-1);
  };

  const salvarBaseCalculoNCM = async () => {
    setIsLoadingPostNcm(true);
    const baseDeCalculoNCM: IOutrasBasesNcmEnviar = {
      idBaseCalculo: context?.idOutrosRegimes || 0,
      codigoNcm: codigoNCM,
      tributosIncidentes: deducoesAdicionadas,
      unidadeMedida: unidadeMedida, // valor do select de unidade de medida
      valorReceitaBruta: removeFormatCurrency(receitaBruta), // valor do input de receita bruta
      isRejeito: isRejeito,
      tipoDeUso: parseInt(tipoUso.toString()),
      hasUsoEspecifico: hasUsoEspecifico,
      quantidade: parseFloat(quantidade), // valor do input de quantidade
      possuiAfetacaoDutovia: hasDutovia,
      modalidadesAfetacaoFerrovia: hasFerrovia
        ? ferroviasAdicionadas.map(
            (ferrovia): IFerroviaEnviar => ({
              idFerrovia: ferrovia.idFerrovia,
              municipioOrigem: ferrovia.origem.codigoIBGE,
              municipioDestino: ferrovia.destino.codigoIBGE,
            })
          )
        : [],
      modalidadesAfetacaoPorto: hasOperacoesPortuarias
        ? operacoesPortuariasAdicionadas.map(
            (operacao): IOperacaoPortuariaEnviar => ({
              idPorto: operacao.idPorto,
            })
          )
        : [],
      modalidadesAfetacaoMunicipioMineracao: hasEstruturasFora
        ? estruturasAdicionadas.map(
            (estrutura): IEstruturaEnviar => ({
              municipioMineracao: estrutura.codigoIBGE,
            })
          )
        : [],
    };

    const msgErroValidacao = validarOutrasBasesCalculoForm(
      baseDeCalculoNCM,
      converterDeducoesParaArrayString(deducoesData),
      haCompraTerceiro
    );

    if (msgErroValidacao != '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
      setIsLoadingPostNcm(false);
      return;
    }

    if (isEditNcm) {
      await putNCMOutrasBasesCalculo(baseDeCalculoNCM, context?.idNCMOutrosRegimes || -1)
        .then(() => {
          limparFormulario();
          mostrarNotificacao({
            tipo: 'success',
            conteudo: `NCM salvo com sucesso.`,
          });
          ncmsRefetch();
          setIsEditNcm(false);
          setNcmEdicao(null);
          context?.setIdNCMOutrosRegimes(-1);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: `Erro ao salvar NCM, tente novamente.`,
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setIsLoadingPostNcm(false);
        });
    } else {
      await postNCMOutrasBasesCalculo(baseDeCalculoNCM)
        .then(() => {
          limparFormulario();
          mostrarNotificacao({
            tipo: 'success',
            conteudo: `NCM adicionado com sucesso.`,
          });
          ncmsRefetch();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: `Erro ao adicionar NCM, tente novamente.`,
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setIsLoadingPostNcm(false);
        });
    }
  };

  const editNCM = (ncm: IOutrasBasesNcmReceber, id: number) => {
    context?.setIdNCMOutrosRegimes(id);
    setNcmEdicao(ncm);
    setCodigoNCM(ncm.codigoNcm);
    setReceitaBruta(formatCurrency(ncm.valorReceitaBruta));
    setQuantidade(ncm.quantidade.toString());
    setUnidadeMedida(ncm.unidadeMedida);
    setIsRejeito(ncm.isRejeito);
    setHasUsoEspecifico(ncm.hasUsoEspecifico);
    setHaCompraTerceiro(
      ncm.tributosIncidentes.find((ti) => ti.nome === COMPRAS_DE_TERCEIRO && ti.valor !== 0) !== undefined
        ? true
        : false
    );
    setDeducoesAdicionadas(
      ncm.tributosIncidentes.map((deducao) => ({ nome: deducao.nome, valor: deducao.valor ?? 0 }))
    );
    setFerroviasAdicionadas(
      ncm?.modalidadesAfetacaoFerrovia?.map((modalidade) => ({
        idFerrovia: modalidade.ferrovia.id,
        nomeFerrovia: modalidade.ferrovia.nome,
        origem: modalidade.municipioOrigem,
        destino: modalidade.municipioDestino,
      })) || []
    );
    setOperacoesPortuariasAdicionadas(
      ncm?.modalidadesAfetacaoPorto?.map((operacao) => ({
        idPorto: operacao.porto.id,
        municipio: operacao.porto.uf + ' - ' + operacao.porto.cidade,
      })) || []
    );
    setEstruturasAdicionadas(
      ncm?.modalidadesAfetacaoMunicipioMineracao?.map((estrutura) => ({
        codigoIBGE: estrutura.municipioMineracao.codigoIBGE,
        uf: estrutura.municipioMineracao.uf,
        nome: estrutura.municipioMineracao.nome,
      })) || []
    );
    setHasDutovia(ncm?.possuiAfetacaoDutovia);
    setHasEstruturasFora(
      ncm?.modalidadesAfetacaoMunicipioMineracao && ncm.modalidadesAfetacaoMunicipioMineracao.length > 0
    );
    setHasFerrovia(ncm?.modalidadesAfetacaoFerrovia && ncm.modalidadesAfetacaoFerrovia.length > 0);
    setHasOperacoesPortuarias(ncm?.modalidadesAfetacaoPorto && ncm.modalidadesAfetacaoPorto.length > 0);

    setTipoUso(ncm.tipoDeUso.id);
    setIsEditNcm(true);
  };

  const receberInformacoes = async () => {
    setIsLoadingInfo(true);

    const baseDeCalculoNCM: IOutrasBasesNcmEnviar = {
      idBaseCalculo: context?.idOutrosRegimes || 0,
      codigoNcm: codigoNCM,
      tributosIncidentes: deducoesAdicionadas,
      unidadeMedida: unidadeMedida, // valor do select de unidade de medida
      valorReceitaBruta: removeFormatCurrency(receitaBruta), // valor do input de receita bruta
      isRejeito: isRejeito,
      hasUsoEspecifico: hasUsoEspecifico,
      tipoDeUso: tipoUso,
      quantidade: parseFloat(quantidade), // valor do input de quantidade
      possuiAfetacaoDutovia: hasDutovia,
      modalidadesAfetacaoFerrovia: hasFerrovia
        ? ferroviasAdicionadas.map(
            (ferrovia): IFerroviaEnviar => ({
              idFerrovia: ferrovia.idFerrovia,
              municipioOrigem: ferrovia.origem.codigoIBGE,
              municipioDestino: ferrovia.destino.codigoIBGE,
            })
          )
        : [],
      modalidadesAfetacaoPorto: hasOperacoesPortuarias
        ? operacoesPortuariasAdicionadas.map(
            (operacao): IOperacaoPortuariaEnviar => ({
              idPorto: operacao.idPorto,
            })
          )
        : [],
      modalidadesAfetacaoMunicipioMineracao: hasEstruturasFora
        ? estruturasAdicionadas.map(
            (estrutura): IEstruturaEnviar => ({
              municipioMineracao: estrutura.codigoIBGE,
            })
          )
        : [],
    };

    const msgErroValidacao = validarOutrasBasesCalculoForm(
      baseDeCalculoNCM,
      converterDeducoesParaArrayString(deducoesData),
      haCompraTerceiro
    );

    if (msgErroValidacao != '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
      setIsLoadingInfo(false);
      return;
    }

    const body: IAtualizarInformacoesEnviar = {
      idGuiaRecolhimento: guiaRecolhimentoId,
      valorReceitaBruta: removeFormatCurrency(receitaBruta),
      valorTotalTributos: Number(deducoesAdicionadas.reduce((acc, deducao) => acc + deducao.valor, 0).toFixed(2)),
      isRejeitoOuEsteril: isRejeito,
      hasUsoEspecifico: hasUsoEspecifico,
      tipoDeUso: tipoUso,
    };

    await postAtualizarInformacoes(body)
      .then((res) => {
        setInfo([
          { label: 'Base de Cálculo(R$)', value: res?.baseCalculo },
          {
            ...info[1],
            label: 'Tipo de Alíquota',
            value: res?.resultadoAliquota?.tipoAliquota,
          },
          { label: 'Alíquota(%)', value: res?.resultadoAliquota?.valorAliquota, format: formatPercentual },
          { label: 'CFEM Total a Recolher(R$)', value: res?.valorCfem },
        ]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
        setIsLoadingInfo(false);
      });
  };

  const getReceitaLabel = (nomeBaseCalculo: PaginasOutrosRegimes) => {
    switch (nomeBaseCalculo) {
      case 'receitaBruta':
        return 'Receita Bruta de Vendas (R$)';
      case 'vendaNacional':
        return 'Receita Bruta de Vendas (R$)';
      case 'receitaExportacoes':
        return 'Receita Calculada (R$)';
      case 'receitaBrutaCalculada':
        return 'Receita Bruta Calculada (R$)';
      default:
        return 'Receita Bruta';
    }
  };

  const isTipoDeUsoEspecificoDisabled = (tiposUsoData as ITipoDeUsoLista)?.tipoDeUsoEspecifico.length === 0;

  return (
    <div className={styles.container}>
      {isLoadingPostBase || isPadraoAguaMineralIsLoading ? (
        <BrLoading large />
      ) : (
        <>
          <div className={styles.content}>
            <div className={styles.titleContainer}>
              <h4 className={styles.title}>{isEdicao ? 'Editar' : 'Nova'} Base de Cálculo</h4>
              <span className={styles.subTitle}>Operação - {NomesPaginasOutrosRegimes[nomeBaseCalculo]}</span>
            </div>
            <div className={styles.dadosTriadeContainer}>
              <Row>
                <Col>
                  <span className={styles.label}>Competência</span>
                  <p className={styles.text}>{competencia}</p>
                </Col>
                <Col>
                  <span className={styles.label}>Processo Minerário</span>
                  <p className={styles.text}>{formatarNrProcesso(context?.numeroProcesso || '')}</p>
                </Col>
                <Col>
                  <span className={styles.label}>Substância</span>
                  <p className={styles.text}>{context?.substancia.toString()}</p>
                </Col>
                <Col>
                  <span className={styles.label}>Município/UF</span>
                  <p className={styles.text}>
                    {context?.municipio.nome}/{context?.municipio.uf}
                  </p>
                </Col>
              </Row>
            </div>
            <div className={styles.form}>
              <div className={styles.formTitle}>{isEditNcm ? 'Editar NCM' : 'Adicionar novo NCM'}</div>
              <Row>
                <Col>
                  <AnmSelectQuery
                    label={nomeBaseCalculo === 'receitaBrutaCalculada' ? 'Código NCM do Bem Mineral' : 'Código NCM'}
                    value={codigoNCM}
                    setValue={setCodigoNCM}
                    functionQuery={() => getNcmPorGuiaRecolhimento(guiaRecolhimentoId)}
                    keyQuery={['getNcmPorGuiaRecolhimento', guiaRecolhimentoId]}
                    formatResult={(result: INcmListagem[]) =>
                      result.map((ncm: INcmListagem) => ({
                        label: ncm.codigo + ' - ' + ncm.descricao,
                        value: ncm.codigo,
                      }))
                    }
                    placeholder='Selecione o NCM'
                    hintText={getHintText('NCM')}
                  />
                </Col>
                <Col>
                  <BrInput
                    autoComplete='off'
                    label={getReceitaLabel(nomeBaseCalculo)}
                    value={receitaBruta}
                    onChange={(e) => setReceitaBruta(e.target.value)}
                    numeric={{
                      prefix: 'R$ ',
                      thousandSeparator: '.',
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                    maxLength={20}
                  />
                </Col>
                <Col>
                  <BrInput
                    autoComplete='off'
                    label='Quantidade'
                    value={quantidade}
                    onChange={(e) => setQuantidade(e.target.value.replace(',', '.'))}
                    numeric={{
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                    maxLength={14}
                  />
                </Col>
              </Row>
              <Row className={styles.row}>
                <Col size={4}>
                  <AnmSelectQuery
                    label='Unidade de Medida'
                    value={unidadeMedida}
                    setValue={setUnidadeMedida}
                    functionQuery={() => getUnidadesMedidaPorGuiaRecolhimento(guiaRecolhimentoId)}
                    keyQuery={['getUnidadesMedidaPorGuiaRecolhimento', guiaRecolhimentoId]}
                    formatResult={(result: string[]) =>
                      result.map((unidade: string) => ({
                        label: unidade,
                        value: unidade,
                      }))
                    }
                    placeholder='Selecione a unidade de medida'
                  />
                </Col>
                <Col size={4} className={styles.precoMedioCol}>
                  <span className={styles.label}>Preço Médio</span>
                  <span>
                    {receitaBruta && quantidade
                      ? formatCurrency(removeFormatCurrency(receitaBruta) / parseFloat(quantidade))
                      : '-'}
                  </span>
                </Col>
              </Row>
              {deducoesIsLoading ? (
                <BrLoading />
              ) : deducoesError ? (
                <ErrorMessage
                  errorMessage={'Erro ao carregar a lista de deduções. Por favor, tente novamente.'}
                  onClickFunction={() => {
                    deducoesRefetch();
                  }}
                />
              ) : (
                deducoesData && (
                  <SecaoDeducoes
                    key={codigoNCM}
                    deducoesPossiveis={deducoesData}
                    deducoesAdicionadas={deducoesAdicionadas}
                    setDeducoesAdicionadas={setDeducoesAdicionadas}
                    semMargem
                    setHaCompraTerceiro={setHaCompraTerceiro}
                    haCompraTerceiro={haCompraTerceiro}
                  />
                )
              )}
              <ModalidadeAfetacao
                ferroviasAdicionadas={ferroviasAdicionadas}
                setFerroviasAdicionadas={setFerroviasAdicionadas}
                operacoesPortuariasAdicionadas={operacoesPortuariasAdicionadas}
                setOperacoesPortuariasAdicionadas={setOperacoesPortuariasAdicionadas}
                estruturasAdicionadas={estruturasAdicionadas}
                setEstruturasAdicionadas={setEstruturasAdicionadas}
                hasFerrovia={hasFerrovia}
                setHasFerrovia={setHasFerrovia}
                hasDutovia={hasDutovia}
                setHasDutovia={setHasDutovia}
                hasOperacoesPortuarias={hasOperacoesPortuarias}
                setHasOperacoesPortuarias={setHasOperacoesPortuarias}
                hasEstruturasFora={hasEstruturasFora}
                setHasEstruturasFora={setHasEstruturasFora}
              />
              {isPadraoAguaMineralError ? (
                <ErrorMessage
                  errorMessage={'Erro ao carregar dados. Por favor, tente novamente.'}
                  onClickFunction={() => {
                    isPadraoAguaMineralRefetch();
                  }}
                />
              ) : (
                <div className={styles.criteriosUso} data-tooltip-id='criterios-uso-tooltip'>
                  <div className={styles.titulo}>
                    <span>Critérios de Uso</span>
                  </div>
                  <div className={styles.divider}>
                    <BrDivider />
                  </div>
                  <div className={styles.campos}>
                    <div className={styles.camposContainer}>
                      <div className={styles.switchContainer} data-tooltip-id='rejeitos-tooltip'>
                        <AnmSwitch
                          label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias produtivas?'
                          value={isRejeito}
                          setValue={setIsRejeito}
                          disable={isPadraoAguaMineral === true}
                          tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
                        />
                      </div>
                      <div className={styles.selectContainer}></div>
                    </div>
                    {tiposUsoIsLoading === true && <BrLoading />}
                    {tiposUsoError == null && tiposUsoIsLoading === false && (
                      <div className={styles.camposContainer}>
                        <div className={styles.switchContainer} data-tooltip-id='tipo-uso-tooltip'>
                          <AnmSwitch
                            label='A substância possui uso específico?'
                            value={hasUsoEspecifico}
                            setValue={setHasUsoEspecifico}
                            tooltipText={
                              'Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei 8.001/1990.'
                            }
                            disable={isTipoDeUsoEspecificoDisabled || isPadraoAguaMineral === true}
                          />
                        </div>
                        <form className={styles.selectContainer} autoComplete='off'>
                          <BrSelect
                            label={hasUsoEspecifico ? 'Tipo de Uso Específico' : 'Tipo de Uso Não Específico'}
                            key={`select-tipo-uso-${tipoUso}`} // força reset do select ao mudar entre uso específico e não específico
                            className={styles.select}
                            placeholder={placeHolderTipoUso}
                            value={tipoUso}
                            onChange={(value: number) => {
                              setTipoUso(value);
                            }}
                            options={
                              hasUsoEspecifico
                                ? (tiposUsoData as ITipoDeUsoLista).tipoDeUsoEspecifico.map((tipo) => ({
                                    label: tipo.nome,
                                    value: tipo.id,
                                  }))
                                : (tiposUsoData as ITipoDeUsoLista).tipoDeUsoNaoEspecifico.map((tipo) => ({
                                    label: tipo.nome,
                                    value: tipo.id,
                                  }))
                            }
                            disabled={isPadraoAguaMineral === true}
                          />
                        </form>
                      </div>
                    )}
                    {tiposUsoError && (
                      <div className={styles.errorContainer}>
                        <span className={styles.errorText}>Não foi possível carregar os dados</span>
                        <BrButton size='small' onClick={() => tiposUsoRefetch()}>
                          (Tentar novamente)
                        </BrButton>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Tooltip
                opacity={1}
                id='rejeitos-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => isPadraoAguaMineral !== true}
              >
                <div className={styles.tooltipText}>
                  Essa opção está marcada como "Não" pois a substância Água Mineral não pode ser classificada como bem
                  mineral oriundo de rejeitos e estéreis.
                </div>
              </Tooltip>
              <Tooltip
                opacity={1}
                id='tipo-uso-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => !(isTipoDeUsoEspecificoDisabled || isPadraoAguaMineral === true)}
              >
                <div className={styles.tooltipText}>
                  Essa opção está marcada como "Não" pois a substância mineral selecionada não se enquadra na condição
                  "destinada ao uso imediato na construção civil" ou "rochas ornamentais".
                </div>
              </Tooltip>
              <AtualizarInformacoes
                onClickFunction={() => receberInformacoes()}
                info={info}
                widthFitContent={false}
                isLoading={isLoadingInfo}
              />
              <div className={styles.containerButton}>
                {isEditNcm && (
                  <BrButton secondary onClick={() => voltar()} icon='arrow-left'>
                    Voltar
                  </BrButton>
                )}
                <BrButton secondary onClick={() => salvarBaseCalculoNCM()} icon='plus' loading={isLoadingPostNcm}>
                  {isEditNcm ? 'Salvar Alterações do NCM' : 'Adicionar NCM à Base de Cálculo'}
                </BrButton>
              </div>
            </div>

            {!isEditNcm && (
              <BrCard className={styles.cardSecao}>
                <div className={styles.cardSecaoContent}>
                  <span className={styles.cardSecaoTitle}>Lista de NCMs adicionados</span>
                  {ncmsIsLoading ? (
                    <BrLoading large />
                  ) : (
                    <>
                      {ncmsError ? (
                        <ErrorMessage
                          errorMessage={'Erro ao carregar a lista de NCM. Por favor, tente novamente.'}
                          onClickFunction={() => {
                            ncmsRefetch();
                          }}
                        />
                      ) : (
                        <div className={styles.cardSecaoList}>
                          {ncmsData && ncmsData.length > 0 ? (
                            ncmsData.map((ncm: IOutrasBasesNcmReceber) => (
                              <NcmAccordion
                                isEditable={true}
                                ncm={ncm}
                                editFunction={editNCM}
                                refetchNCMList={ncmsRefetch}
                                tipoBaseCalculo={nomeBaseCalculo}
                              />
                            ))
                          ) : (
                            <div>Nenhum NCM ainda foi adicionado.</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </BrCard>
            )}
          </div>
          {!isEditNcm && (
            <div className={styles.containerButton}>
              <BrButton
                secondary
                onClick={() => {
                  setIsEdicao(false);
                  setNcmEdicao(null);
                  setIsAdicaoNovaBaseCalculo(false);
                }}
              >
                <span className={styles.button}>
                  <i className={`fas fa-times`} /> Cancelar
                </span>
              </BrButton>
              <BrButton
                onClick={() => {
                  if (ncmsData && ncmsData.length > 0) {
                    setIsEdicao(false);
                    setNcmEdicao(null);
                    setIsAdicaoNovaBaseCalculo(false);
                  } else {
                    mostrarNotificacao({
                      tipo: 'danger',
                      conteudo: `Adicione ao menos um NCM para salvar a base de cálculo.`,
                    });
                  }
                }}
                primary
              >
                <span className={styles.button}>
                  {isEdicao ? (
                    <>
                      <i className={`fas fa-save`} /> Salvar Base de Cálculo
                    </>
                  ) : (
                    <>
                      <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                    </>
                  )}
                </span>
              </BrButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OutrasBaseCalculoForm;
